import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Observable } from 'rxjs/internal/Observable';
import { Subject } from 'rxjs/internal/Subject';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';

export interface SubApi {
  items: any[];
  total_count: number;
}

export type HireProgressStatus = 'pending' | 'active' | 'done' | 'error';

export interface HireProgressStep {
  key: string;
  label: string;
  status: HireProgressStatus;
  detail?: string;
}

@Injectable({ providedIn: 'root' })
export class SubService {
  private currentSub;

  public currentSearch = {
    params: null,
    results: null
  };

  private API_URL = '/api/subs/';

  private headerOptions = {headers: new HttpHeaders({'Content-Type': 'application/json'})};

  public subStatuses = null;
  public subHireProcessStep: string = ''; // For sub hire process custom spinner with content step text (legacy single-line text).

  // Structured, observable progress for the sub-hire process so the UI can render a live step checklist.
  public hireProgress$ = new BehaviorSubject<HireProgressStep[]>([]);

  constructor(
    private httpClient: HttpClient
  ) { }


  // The ordered checklist shown while a sub is being hired. Keys are referenced by setHireStep() below.
  private defaultHireSteps(): HireProgressStep[] {
    return [
      { key: 'provider', label: 'Provider record', status: 'pending' },
      { key: 'placement', label: 'Placement record', status: 'pending' },
      { key: 'credentialing', label: 'Credentialing setup', status: 'pending' },
      { key: 'agreements', label: 'Onboarding agreements', status: 'pending' },
      { key: 'items', label: 'Required items', status: 'pending' }
    ];
  }


  // Reset the checklist to all-pending at the start of a hire.
  initHireProgress(): void {
    this.subHireProcessStep = '';
    this.hireProgress$.next(this.defaultHireSteps());
  }


  // Move a step to a new status. Setting a step 'active' marks any earlier unfinished steps 'done'
  // so the checklist always reflects forward progress even if a step's start wasn't explicitly signalled.
  setHireStep(key: string, status: HireProgressStatus, detail?: string): void {
    const steps = this.hireProgress$.value.map(_s => ({ ..._s }));
    const target = steps.find(_s => _s.key === key);
    if (!target) return;

    if (status === 'active') {
      for (const _s of steps) {
        if (_s.key === key) break;
        if (_s.status === 'pending' || _s.status === 'active') _s.status = 'done';
      }
    }

    target.status = status;
    if (detail !== undefined) target.detail = detail;

    this.subHireProcessStep = detail ?? target.label; // Keep legacy text field in sync.
    this.hireProgress$.next(steps);
  }


  // Mark every non-errored step done (called once the whole hire succeeds).
  completeHireProgress(): void {
    const steps = this.hireProgress$.value.map(_s => ({ ..._s, status: (_s.status === 'error' ? _s.status : 'done') as HireProgressStatus }));
    this.hireProgress$.next(steps);
  }

  setCurrent(_sub): void {
    this.currentSub = _sub;
  }

  getCurrent() {
    return this.currentSub;
  }


  create(_sub): Observable<any> {
    return this.httpClient.post<any>(this.API_URL, JSON.stringify(_sub), this.headerOptions);
  }


  // updateFieldsByIdArray(arrayOfIds, fieldsToUpdate): Observable<any[]> {
  //   const updateRequest = {ids: arrayOfIds, fields: fieldsToUpdate};
  //   return this.httpClient.put<any[]>(this.API_URL + 'update-certain-fields/', JSON.stringify(updateRequest), this.headerOptions);
  // }


  updateFieldsByIdArray(arrayOfIds, fieldsToUpdate): Observable<any[]> {
    const updateRequest = {ids: arrayOfIds, fields: fieldsToUpdate};
    return this.httpClient.put<any[]>(this.API_URL + 'update-certain-fields-array/', JSON.stringify(updateRequest), this.headerOptions);
  }


  updateFieldsById(_id, fieldsToUpdate): Observable<SubApi> {
    return this.httpClient.put<SubApi>(this.API_URL + 'update-certain-fields/' + _id, JSON.stringify(fieldsToUpdate), this.headerOptions);
  }


  update(_sub): Observable<any> {
    return this.httpClient.put<any>(this.API_URL + _sub._id, JSON.stringify(_sub), this.headerOptions);
  }


  updateFavorites(_sub): Observable<any> {
    return this.httpClient.put<any>(this.API_URL + 'favorite/' + _sub._id, JSON.stringify(_sub), this.headerOptions);
  }


  normalSearch(searchTerms): Observable<any[]> {
    searchTerms['selectAll'] = true;
    return this.httpClient.post<any[]>(this.API_URL + 'normal-search', JSON.stringify(searchTerms), this.headerOptions);
  }


  customRecruiterStatSearch(searchTerms): Observable<any[]> {
    searchTerms['selectAll'] = false;
    return this.httpClient.post<any[]>(this.API_URL + 'recruiter-stat-search', JSON.stringify(searchTerms), this.headerOptions);
  }


  getSub(_id: string): Observable<any> {
    return this.httpClient.get<any>(this.API_URL + _id);
  }


  getWithRelationships(_moduleOptions): Observable<any> {
    return this.httpClient.post<any>(this.API_URL + 'with-relationships/' + _moduleOptions.searchTerms._id, JSON.stringify(_moduleOptions), this.headerOptions);
  }


  delete(id: string): Observable<any> {
    return this.httpClient.put(this.API_URL + 'mark-deleted/' + id, this.headerOptions);
  }


  selectAllSearch(searchTerms): Observable<SubApi> {
    searchTerms['selectAll'] = true;
    searchTerms['selectNumberOfRecords'] = false;
    searchTerms['searchTerms']['idsOnly'] = false; // Incase it is set on searchTerms.
    return this.httpClient.post<SubApi>(this.API_URL + 'dataSourceSearch', JSON.stringify(searchTerms), this.headerOptions);
  }


  selectNumberOfRecordsSearch(searchParams): Observable<any> {
    searchParams['selectAll'] = false;
    searchParams['selectNumberOfRecords'] = true;
    searchParams['idsOnly'] = false; // For only returning basic lightweight version of results if we don't need all fields.
    return this.httpClient.post<any>(this.API_URL + 'dataSourceSearch', JSON.stringify(searchParams), this.headerOptions);
  }


  getCustomDatasource(searchParams, sortField = 'name', sortDirection = 'asc', pageNumber: number = 0, pageSize: number = 10): Observable<SubApi> {
    searchParams['selectAll'] = false;
    searchParams['selectNumberOfRecords'] = false;
    searchParams['idsOnly'] = false;

    searchParams.sortField = sortField;
    searchParams.sortOrder = sortDirection;
    searchParams.pageNumber = pageNumber;
    searchParams.pageSize = pageSize;

    return this.httpClient.post<SubApi>(this.API_URL + 'dataSourceSearch', JSON.stringify(searchParams), this.headerOptions); 
  }
}